import {
  ApiResponse,
  ProgressDO,
  ResponseErrorDO,
} from 'src/components/visionx/integration-status/types';
import { QueryParamsDTO } from 'src/components/types';
import { VXCommentDO } from 'src/components/visionx/vx-comments/types.tsx';

export interface AvailableAnswerDO {
  score: number;
  displayName: string;
}

export interface AvailableAnswersDO {
  [questionId: number]: AvailableAnswerDO[];
}

export interface SurveysResultDO {
  archived: boolean;
  isComplete: boolean;
  dateCreated: string;
  dateUpdated: string;
  id: string;
  percentComplete: number;
  title: string;
  userId: string;
  userName: string;
  cyFunLevel: CyFunLevel;
}

export interface SelfAssessmentResultDO extends SurveysResultDO {
  comments: VXCommentDO[];
  history: HistoryEntryDO[];
  ownTarget: OwnTargetDO;
  questions: QuestionDO[];
  summaryScores: SummaryScoresDO;
  blockDetails: BlockDetailDO[];
}

export interface SurveysResultsDO {
  content: SurveysResultDO[];
  queryParams: QueryParamsDTO;
  total: number;
}

export interface SurveysResultsDTO extends ProgressDO, ResponseErrorDO {
  data: SurveysResultsDO;
}

export interface SelfAssessmentResultsDO {
  results: SelfAssessmentResultDO[];
}

export interface SelfAssessmentDTO extends ProgressDO, ResponseErrorDO {
  data: SelfAssessmentResultsDO;
}

export interface QuestionDO {
  block: BlockDO;
  category: CategoryDO;
  subCategoryDO: SubCategoryDO;
  question: QuestionDetailsDO;
}

export interface BlockDO {
  id: string;
  sortOrder: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  conclusion: string;
}

export interface BlockDetailDO extends BlockDO {
  conclusion: string;
}

export interface CategoryDO {
  id: string;
  sortOrder: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  code: string;
  blockId: string;
}

export interface SubCategoryDO {
  id: string;
  sortOrder: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  code: string;
  categoryId: string;
}

export interface QuestionDetailsDO {
  id: number;
  sortOrder: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  targetScore: number;
  subCategoryId: string;
  customerAnswer: number | null;
  customerValidation: string | null;
  answers: AvailableAnswerDO[];
  risk: string;
  recommendation: string;
}

export interface HistoryEntryDO {
  id: string;
  resultsId: string;
  date: string;
  detect: number;
  identify: number;
  protect: number;
  recover: number;
  respond: number;
  govern: number;
  overall: number;
}

export interface OwnTargetDO {
  id: string | undefined;
  surveyId: string;
  updatedBy: string;
  updatedAt: string;
  detect: number;
  identify: number;
  protect: number;
  recover: number;
  respond: number;
  govern: number;
  cyFunLevel: CyFunLevel;
}

export interface OwnTargetScoreDO {
  detect: number;
  identify: number;
  protect: number;
  recover: number;
  respond: number;
  govern: number;
  surveyId?: string;
}

export interface SummaryScoresDO {
  blockScores: {
    target: ScoreMapDO;
    customer: ScoreMapDO;
    scoresAsList: ScoreMapDO[];
  };
  categoryScores: {
    [key: string]: {
      target: ScoreMapDO;
      customer: ScoreMapDO;
      scoresAsList: ScoreMapDO[];
    };
  };
}

//{customer: {labels: [], scores: []}, target: {labels: [], scores: []}}
export interface ScoreMapDO {
  theMap: {
    [key: string]: number;
  };
  name: string;
}

export interface CustomerBlockDO {
  labels: string[];
  scores: number[];
  title: string;
  surveyId: string;
  cyFunLevel: CyFunLevel;
}

export interface TargetBlockDO {
  labels: string[];
  scores: number[];
}

export interface BlockScoresDO {
  target: TargetBlockDO;
  customer: TargetBlockDO;
}

export interface CustomerCategoryDO {
  scores: number[];
  labels: string[];
  title: string;
  targetArray: number[];
  surveyId: string;
  cyFunLevel: CyFunLevel;
}

export interface InternalAnswersDO {
  [blockId: string]: {
    [categoryId: string]: {
      [subCategoryId: string]: {
        [questionId: string]: number | string;
      };
    };
  };
}

export interface InternalQuestionDO {
  question: string;
  questionId: number;
  indexInQuestionsArray: number;
}

export interface InternalSubCategoryDO {
  [subCategory: string]: InternalQuestionDO[];
}

export interface InternalCategoryDO {
  [category: string]: InternalSubCategoryDO;
}

export interface InternalFunctionsDO {
  [block: string]: InternalCategoryDO;
}

export interface CategoryCodesDO {
  [key: string]: string;
}

export interface FrameworkCodesDO {
  [key: string]: CategoryCodesDO;
}

interface CategoryRatings {
  [categoryCode: string]: number;
}

export interface TargetScoreRatingsDO {
  [blockId: string]: CategoryRatings;
}

export type RecordStringStringDO = Record<string, string>;

export interface SelfAssessmentLineChartDataDO {
  selfAssessment: {
    Total: {
      [index: string]: string;
    };
    Identify: {
      [index: string]: string;
    };
    Protect: {
      [index: string]: string;
    };
    Detect: {
      [index: string]: string;
    };
    Respond: {
      [index: string]: string;
    };
    Recover: {
      [index: string]: string;
    };
    Govern: {
      [index: string]: string;
    };
    Target: {
      [index: string]: string;
    };
  };
  labels: {
    [index: string]: string;
  };
}

export const emptySALineData: SelfAssessmentLineChartDataDO['selfAssessment'] = {
  Total: {},
  Identify: {},
  Protect: {},
  Detect: {},
  Respond: {},
  Recover: {},
  Govern: {},
  Target: {},
};

export interface ContinueSurveyDO {
  title: string;
  percentage: number;
  id: string;
  percentComplete: number;
}

export interface ArraySliceDO {
  start: number;
  end: number;
}

export interface EvidenceResponseDO {
  id: string;
  tenantId: string;
  questionId: string;
  surveyId: string;
  evidence: string;
  created: string;
  updated: string;
  evidenceFileUploads: EvidenceFileUpload[];
}

export interface EvidenceFileUpload {
  fileName: string;
  evidenceFileUrl: string;
}

export interface EvidenceResponseDTO extends ProgressDO, ResponseErrorDO {
  data: EvidenceResponseDO;
}

export interface MaturityAssessmentQuestion {
  question: string;
  questionId: number;
  indexInQuestionsArray: number;
}

export interface DescriptionsDO {
  functions: FunctionDescriptionDO[];
}

export interface DescriptionsResponseDTO extends ProgressDO, ResponseErrorDO {
  data: DescriptionsDO;
}

export interface FunctionDescriptionDO {
  id: number;
  name: string;
  description: string;
  categories: CategoryDescriptionDO[];
}

export interface CategoryDescriptionDO {
  id: number;
  name: string;
  description: string;
  subCategories: SubCategoryDescriptionDO[];
}

export interface SubCategoryDescriptionDO {
  id: number;
  name: string;
  description: string;
}

export type KindOfDescriptionDO = 'function' | 'category' | 'subcategory';

export type CustomerBlock = {
  labels: string[];
  scores: number[];
  title: string;
  surveyId: string;
  cyFunLevel: CyFunLevel;
};

export type CustomerCategory = {
  scores: number[];
  targetArray: number[];
  labels: string[];
  title: string;
  surveyId: string;
  cyFunLevel: CyFunLevel;
};

export type CustomerOwnTarget = {
  id: string | undefined;
  updatedBy: string;
  updatedAt: string;
  detect: number;
  identify: number;
  protect: number;
  recover: number;
  respond: number;
  govern: number;
  surveyId: string;
  cyFunLevel: CyFunLevel;
};
export type SurveyDetail = {
  dateCreated: string;
  dateUpdated: string;
  percentComplete: number;
  title: string;
  userName: string;
  questions: QuestionDO[];
};
export type MergedObject = {
  surveyId: string;
  customerBlock?: CustomerBlock;
  customerCategory?: CustomerCategory;
  surveyDetail?: SurveyDetail;
  customerOwnTarget?: CustomerOwnTarget;
  cyFunLevel: CyFunLevel;
  questions?: QuestionDO[];
};
export type InputData = {
  allCustomerBlocks: CustomerBlock[];
  allCustomerCategories: CustomerCategory[];
  allCustomerOwnTargets: CustomerOwnTarget[];
  categoryLabels: string[];
  surveys: SelfAssessmentResultDO[];
};
export type ValidateAnswerResponse = ApiResponse<string>;

export type ExpertResponseData = { content: string; filename: string };
export type ExportResponse = ApiResponse<ExpertResponseData>;

export type NewSurveyResponseData = { id: string; title: string };
export type NewSurveyResponse = ApiResponse<NewSurveyResponseData>;

export type NewSurveyResponseDataError = {
  code: number;
  error: string;
  exceptionType: string;
  message: string;
};
export type NewSurveyResponseError = ApiResponse<NewSurveyResponseDataError>;

export interface SurveyPrimaryKey {
  id: string;
  tenantId: string;
}

export interface SurveyArchiveResponseData {
  primaryKey: SurveyPrimaryKey;
  dateCreated: string;
  dateUpdated: string;
  dateCompleted: string | null;
  title: string;
  archived: boolean;
  userId: string;
  userName: string;
}

export type SurveyArchiveResponse = ApiResponse<SurveyArchiveResponseData>;

export type CyFunLevel = 'NIST2' | 'BASIC' | 'IMPORTANT' | 'ESSENTIAL';

export type QuestionSheetName =
  | 'Govern'
  | 'Identify'
  | 'Protect'
  | 'Detect'
  | 'Respond'
  | 'Recover';

export interface MetaInfo {
  framework?: string;
  cyFunLevel?: CyFunLevel;
  templateVersion?: string;
  expectedTotalQuestions?: number;
  expectedBySheet?: Partial<Record<QuestionSheetName, number>>;
}
export interface TargetScoreDO {
  cyFunLevel: string;
  targetScore: number;
}

export interface UploadExcelResponseData {
  complete: boolean;
  id: string;
  percentComplete: number;
}

export type UploadExcelResponse = ApiResponse<UploadExcelResponseData>;
